import { useEffect, useMemo, useState } from "react";
import { Routes as RoutesAPI } from "../lib/api";
import { toast } from "sonner";
import { X, Mail, Trash2, Send, Plus, Loader2, Star } from "lucide-react";

/**
 * SendManifestModal — pick a saved recipient or enter a new one, then
 * dispatch the current route plan's Spoke PDF via server-side email.
 *
 * Props:
 *   plan   — { id, name } of the plan whose PDF will be attached.
 *   onClose(fn)  — called after successful send OR on cancel.
 */
export default function SendManifestModal({ plan, onClose }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [customEmail, setCustomEmail] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [saveNew, setSaveNew] = useState(true);

  useEffect(() => {
    RoutesAPI.listEmailContacts()
      .then((list) => setContacts(list || []))
      .catch((e) => toast.error("Nie udało się pobrać zapisanych maili: " + (e.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, []);

  const selectedContact = useMemo(
    () => contacts.find((c) => c.id === selectedId),
    [contacts, selectedId]
  );

  const targetEmail = selectedContact?.email || customEmail.trim();
  const canSend = !!targetEmail && /.+@.+\..+/.test(targetEmail) && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    const tid = toast.loading(`Wysyłam manifest do ${targetEmail}...`);
    try {
      await RoutesAPI.sendPlanEmail(plan.id, {
        email: targetEmail,
        note: note.trim(),
        save_contact: !selectedContact && saveNew,
        label: !selectedContact ? customLabel.trim() : "",
      });
      toast.success(`Manifest wysłany na ${targetEmail}`, { id: tid });
      onClose(true);
    } catch (e) {
      toast.error("Błąd wysyłki: " + (e.response?.data?.detail || e.message), { id: tid });
    } finally {
      setSending(false);
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`Usunąć zapisany email „${contact.email}" z książki adresowej?`)) return;
    try {
      await RoutesAPI.removeEmailContact(contact.id);
      setContacts((prev) => prev.filter((c) => c.id !== contact.id));
      if (selectedId === contact.id) setSelectedId(null);
      toast.success("Kontakt usunięty");
    } catch (e) {
      toast.error("Błąd: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div
      className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="send-manifest-modal"
      onClick={() => !sending && onClose(false)}
    >
      <div
        className="bg-white w-full max-w-lg border-2 border-zinc-900 shadow-[8px_8px_0_0_#18181b] max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-zinc-900 bg-zinc-50">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-brand" />
            <h3 className="font-heading font-black uppercase tracking-tight text-lg">
              Wyślij manifest na email
            </h3>
          </div>
          <button
            onClick={() => onClose(false)}
            data-testid="send-manifest-close"
            className="p-1 hover:bg-zinc-200 transition-colors"
            disabled={sending}
            aria-label="Zamknij"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          <div className="text-xs font-mono text-zinc-500 uppercase tracking-wider">
            Plan: <span className="text-zinc-900 font-bold normal-case">{plan.name}</span>
          </div>

          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">
              Zapisane adresy
            </label>
            {loading ? (
              <div className="text-sm text-zinc-500 py-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Ładuję...
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 py-3 px-3 italic">
                Brak zapisanych adresów — wpisz nowy poniżej, zostanie automatycznie dodany.
              </div>
            ) : (
              <div className="border border-zinc-300 divide-y divide-zinc-200 max-h-52 overflow-y-auto">
                {contacts.map((c) => {
                  const active = selectedId === c.id;
                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${
                        active ? "bg-brand/10" : "hover:bg-zinc-50"
                      }`}
                      data-testid={`contact-row-${c.email}`}
                    >
                      <input
                        type="radio"
                        name="contact"
                        checked={active}
                        onChange={() => {
                          setSelectedId(c.id);
                          setCustomEmail("");
                          setCustomLabel("");
                        }}
                        className="accent-brand"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {c.email}
                          {c.use_count > 0 && (
                            <span
                              className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-mono text-zinc-500"
                              title={`Użyto ${c.use_count} raz(y)`}
                            >
                              <Star className="w-2.5 h-2.5" /> {c.use_count}
                            </span>
                          )}
                        </div>
                        {c.label && (
                          <div className="text-[11px] text-zinc-500 truncate">{c.label}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteContact(c);
                        }}
                        className="p-1 text-zinc-400 hover:text-red-600"
                        title="Usuń z zapisanych"
                        data-testid={`contact-delete-${c.email}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-zinc-200 pt-3">
            <label className="block text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">
              Lub wpisz nowy email
            </label>
            <div className="space-y-2">
              <input
                type="email"
                value={customEmail}
                onChange={(e) => {
                  setCustomEmail(e.target.value);
                  setSelectedId(null);
                }}
                placeholder="np. dyspozytor@firma.pl"
                data-testid="send-manifest-new-email"
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                autoComplete="email"
              />
              <input
                type="text"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder={'Etykieta (opcjonalnie, np. „Kurier Jan Kowalski")'}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                disabled={!customEmail}
              />
              {customEmail && (
                <label className="inline-flex items-center gap-2 text-xs text-zinc-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={saveNew}
                    onChange={(e) => setSaveNew(e.target.checked)}
                    className="accent-brand"
                    data-testid="send-manifest-save-contact"
                  />
                  Zapisz w książce adresowej po wysłaniu
                </label>
              )}
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">
              Wiadomość (opcjonalnie)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Dodatkowe uwagi widoczne w treści maila..."
              data-testid="send-manifest-note"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand resize-none"
            />
          </div>
        </div>

        <div className="border-t-2 border-zinc-900 px-5 py-3 flex items-center justify-between bg-zinc-50">
          <button
            onClick={() => onClose(false)}
            disabled={sending}
            className="px-4 py-2 text-sm font-bold uppercase tracking-wider text-zinc-600 hover:text-zinc-900 disabled:opacity-40"
            data-testid="send-manifest-cancel"
          >
            Anuluj
          </button>
          <button
            onClick={handleSend}
            disabled={!canSend}
            data-testid="send-manifest-submit"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold uppercase tracking-wider bg-brand text-white hover:bg-brand-hover disabled:bg-zinc-300 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Wysyłam..." : "Wyślij PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}
